import { useCallback, useEffect, useState } from 'react';
import type { ModoJogo } from '@core/tipos';
import { MODO_PADRAO, ehModoJogo, modoDisponivel } from '@core/tipos';
import type { Armazem } from '@persistencia/indexeddb';
import { CHAVE_MODO } from '@persistencia/schema';

/**
 * Modo em curso e o portão de desenvolvimento (§22.5).
 *
 * O Nitro e o Átrio estão prontos e testados, mas não são oferecidos ainda: o
 * lançamento é em duas etapas. Eles aparecem quando a aplicação é aberta em
 * `/dev` — ou com `?dev`, que funciona em qualquer host, inclusive nos
 * estáticos que não devolvem o `index.html` para caminhos desconhecidos.
 */

export function ehModoDev(local: { pathname: string; search: string }): boolean {
  if (/(^|\/)dev\/?$/.test(local.pathname)) return true;
  return new URLSearchParams(local.search).has('dev');
}

export interface UsoModo {
  readonly modo: ModoJogo;
  readonly dev: boolean;
  /** `false` até o modo salvo ser lido: antes disso não se monta jogo nenhum. */
  readonly pronto: boolean;
  readonly escolher: (modo: ModoJogo) => void;
}

export function useModo(armazem: Armazem): UsoModo {
  const [dev] = useState(() => ehModoDev(window.location));
  const [modo, setModo] = useState<ModoJogo>(MODO_PADRAO);
  const [pronto, setPronto] = useState(false);

  /* Abre onde a pessoa parou (§22.5). */
  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const salvo = await armazem.ler(CHAVE_MODO);
      if (cancelado) return;
      // Modo salvo que deixou de ser oferecido volta ao padrão: ninguém fica
      // preso num modo que a interface não mostra como sair.
      if (ehModoJogo(salvo) && modoDisponivel(salvo, dev)) setModo(salvo);
      setPronto(true);
    })();
    return () => {
      cancelado = true;
    };
  }, [armazem, dev]);

  const escolher = useCallback(
    (proximo: ModoJogo) => {
      setModo(proximo);
      void armazem.guardar(CHAVE_MODO, proximo);
    },
    [armazem],
  );

  return { modo, dev, pronto, escolher };
}
